/**
 * Venner · Aviso de país.
 *
 * Reemplaza a Orbe Geolocation sin costo ni dependencias:
 *
 *   1. Detecta el país físico del visitante con /cdn-cgi/trace — un endpoint del propio
 *      dominio (Shopify sirve detrás de Cloudflare), sin API key, sin límite de peticiones
 *      y sin enviar datos a terceros. Si fallara, cae a /browsing_context_suggestions.json,
 *      el endpoint nativo de Shopify que ya usa el selector de país del tema.
 *   2. Compara ese país contra las reglas configuradas en el theme editor.
 *   3. Sugiere (o redirige) al dominio correcto, conservando la página equivalente
 *      y los parámetros de campaña para no perder la atribución.
 *
 * El resultado se cachea en localStorage: una sola petición por visitante cada N horas.
 */

(() => {
  const STORAGE = {
    geo: 'venner:geo:v1',
    pref: 'venner:geo:pref:v1',
    seen: 'venner:geo:seen:v1'
  };

  // Rutas donde nunca interrumpimos: el cliente ya está comprando o autenticándose.
  const BLOCKED_PATHS = /^\/(checkouts?|cart\/|account|tools|apps|challenge|password|\d+\/(checkouts|orders))/;

  const BOT = /bot|crawl|spider|slurp|bingpreview|facebookexternalhit|whatsapp|telegram|preview|lighthouse|headless|pingdom|gtmetrix|semrush|ahrefs/i;

  const now = () => Date.now();

  function read(key) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed.expires && parsed.expires < now()) {
        localStorage.removeItem(key);
        return null;
      }
      return parsed.value;
    } catch (error) {
      return null;
    }
  }

  function write(key, value, days) {
    try {
      localStorage.setItem(key, JSON.stringify({ value, expires: now() + days * 864e5 }));
    } catch (error) {
      /* Modo privado: seguimos sin persistencia. */
    }
  }

  /* ------------------------------------------------------------------ detección */

  async function fromCloudflare(signal) {
    const response = await fetch('/cdn-cgi/trace', { signal, credentials: 'omit' });
    if (!response.ok) return null;
    const match = (await response.text()).match(/^loc=([A-Z]{2})$/m);
    return match ? match[1] : null;
  }

  async function fromShopify(signal) {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const response = await fetch(
      `${root}browsing_context_suggestions.json?source=geolocation_recommendation&country[enabled]=true`,
      { signal, headers: { Accept: 'application/json' } }
    );
    if (!response.ok) return null;
    const data = await response.json();
    return (data && data.detected_values && data.detected_values.country && data.detected_values.country.handle) || null;
  }

  async function detectCountry(cacheHours) {
    const cached = read(STORAGE.geo);
    if (cached) return cached;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 3500);

    try {
      const country = (await fromCloudflare(controller.signal).catch(() => null)) ||
        (await fromShopify(controller.signal).catch(() => null));
      if (country) write(STORAGE.geo, country, cacheHours / 24);
      return country;
    } finally {
      clearTimeout(timer);
    }
  }

  /* ------------------------------------------------------------------ destino */

  /**
   * Traduce la página actual a su equivalente en el dominio destino.
   *
   * Los handles coinciden en la mayoría de los productos pero no en todos, así que el
   * enlace viaja con `vg_h` y `vg_q`: si la página no existe allá, la plantilla 404 de
   * esa tienda usa esos datos para mandar al cliente a la búsqueda en vez de a un error.
   */
  function buildTarget(rule, config, chosenCountry) {
    const base = rule.url.replace(/\/+$/, '');
    const params = new URLSearchParams();
    let path = '/';

    if (config.deepLink) {
      // Quitamos el prefijo de idioma/mercado: el dominio destino tiene el suyo.
      const clean = location.pathname.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/|$)/i, '') || '/';
      if (/^\/(products|collections|pages|blogs)\//.test(clean)) path = clean;
    }

    // Conservamos la atribución de campaña: sin esto la venta se registra como directa.
    const incoming = new URLSearchParams(location.search);
    for (const [key, value] of incoming) {
      if (/^(utm_|gclid|fbclid|ttclid|msclkid|irclickid|epik)/i.test(key)) params.set(key, value);
    }

    params.set('vg_from', config.originCode);
    // La otra tienda usa esto para dejar al cliente en el market que eligió aquí.
    if (chosenCountry) params.set('vg_country', chosenCountry);
    if (path !== '/') {
      params.set('vg_h', path.split('/').filter(Boolean).pop());
      if (config.pageTitle) params.set('vg_q', config.pageTitle);
    }

    const query = params.toString();
    return base + path + (query ? `?${query}` : '');
  }

  /**
 * Nunca sugerir la tienda en la que ya estamos. Sin esto, un visitante de un país sin
 * cobertura vería en la tienda de respaldo un aviso invitándolo a la tienda de respaldo.
 */
function sameOrigin(url) {
  try {
    return new URL(url, location.href).origin === location.origin;
  } catch (error) {
    return false;
  }
}

/* ------------------------------------------------------------------ analítica */

  function track(action, detail) {
    const payload = { event: 'venner_geo', geo_action: action, ...detail };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    document.dispatchEvent(new CustomEvent('venner:geo', { detail: payload }));
  }

  /* ------------------------------------------------------------------ elemento */

  class VennerGeo extends HTMLElement {
    connectedCallback() {
      this.config = JSON.parse(this.querySelector('[data-venner-geo-config]').textContent);
      this.dialog = this.querySelector('.venner-geo__backdrop');

      this.querySelectorAll('[data-venner-geo-dismiss]').forEach((node) => {
        node.addEventListener('click', () => this.dismiss());
      });
      this.querySelector('[data-venner-geo-go]').addEventListener('click', (event) => {
        event.preventDefault();
        this.go();
      });
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.dismiss();
      });

      this.picker = this.querySelector('[data-venner-geo-select]');
      if (this.picker) {
        this.picker.addEventListener('change', () => this.onPick());
        this.buildCombo();
      }

      this.applyIncomingCountry();

      // En el theme editor no se abre solo: taparía el resto de la página mientras se edita. Se muestra
      // solo al seleccionar la sección, igual que los popups del tema base.
      if (window.Shopify && Shopify.designMode) {
        const section = this.closest('.shopify-section');

        if (!section) {
          return;
        }

        section.addEventListener('shopify:section:select', () => {
          // `showModal()` lanza un error si el diálogo ya está abierto, y el editor puede emitir
          // varios `select` seguidos al ir saltando entre los bloques de la sección.
          if (this.hasAttribute('open')) {
            return;
          }

          this.rule = this.config.rules[0];
          if (this.rule) this.render('MX').open();
        });

        section.addEventListener('shopify:section:deselect', () => this.close());

        return;
      }

      this.evaluate();
    }

    /**
     * El aviso de país decide a qué tienda pertenece el visitante, así que va primero: otros
     * popups (la raspadita) esperan esta señal para no taparlo. Se emite tanto si el aviso
     * se descarta como si resulta que no aplicaba.
     */
    settle() {
      if (this.navigating || this.dataset.state === 'done') return;
      this.dataset.state = 'done';
      document.dispatchEvent(new CustomEvent('venner:geo:settled'));
    }

    async evaluate() {
      try {
        await this.decide();
      } finally {
        // Si no quedó nada en pantalla, el resto de la página puede seguir con lo suyo.
        if (!this.hasAttribute('open')) this.settle();
      }
    }

    async decide() {
      const search = new URLSearchParams(location.search);

      // Herramientas de prueba, para verificar sin necesidad de un VPN:
      //   ?vg_reset=1     borra la decisión guardada y el país cacheado
      //   ?vg_test=US     simula que el visitante entra desde ese país
      if (search.has('vg_reset')) {
        Object.values(STORAGE).forEach((key) => localStorage.removeItem(key));
      }

      const simulated = (search.get('vg_test') || '').toUpperCase().slice(0, 2);
      let preference = null;

      if (!simulated) {
        if (BOT.test(navigator.userAgent) || navigator.webdriver) return;
        if (BLOCKED_PATHS.test(location.pathname)) return;
        if (search.has('vg_from')) return; // Ya viene de la otra tienda.

        preference = read(STORAGE.pref);
        if (preference === 'stay') return;
        if (read(STORAGE.seen)) return;
      }

      const country = simulated || (await detectCountry(this.config.cacheHours));
      if (!country) return;

      this.rule = this.resolve(country);
      if (!this.rule) return;

      track('detected', { geo_country: country, geo_target: this.rule.url });

      // Quien ya eligió cambiarse de tienda no tiene por qué volver a decidirlo.
      if (this.config.mode === 'auto' || preference === 'go') {
        this.go(true);
        return;
      }

      this.render(country).open();
      track('shown', { geo_country: country });
    }

    /**
     * Llegada desde la otra tienda con un país ya elegido: se aplica ese market.
     * El guardia contra el país actual evita que el formulario se reenvíe en bucle.
     */
    applyIncomingCountry() {
      const wanted = (new URLSearchParams(location.search).get('vg_country') || '').toUpperCase();
      if (!wanted || wanted === this.config.currentCountry) return;
      if (!(this.config.localCountries || []).includes(wanted)) return;
      this.submitCountry(wanted);
    }

    /**
     * Deja constancia de que el visitante ya decidió. Sin esto el aviso vuelve a salir
     * en la siguiente carga, que es justo lo que pasa al cambiar de market: el
     * formulario recarga la página y el aviso no encontraba nada en memoria.
     */
    remember(decision) {
      write(STORAGE.pref, decision, this.config.rememberDays);
      write(STORAGE.seen, 1, this.config.snoozeDays);
    }

    /** Cambia de market sin salir del dominio, con el formulario nativo de Shopify. */
    submitCountry(iso) {
      const input = this.querySelector('[data-venner-geo-country-input]');
      if (!input || !input.form) return;
      input.value = iso;
      // La página se va a recargar: no hay que avisarle a nadie de que el aviso terminó.
      this.navigating = true;
      track('market_change', { geo_country: iso });
      // Eligió quedarse en esta tienda: se registra antes de recargar.
      this.remember('stay');
      input.form.submit();
    }

    /** El visitante eligió un país en el selector. */
    onPick() {
      const option = this.picker.selectedOptions[0];
      if (!option) return;

      const iso = option.value;
      const name = option.textContent.trim();
      const remote = option.dataset.scope === 'remote';
      const go = this.querySelector('[data-venner-geo-go]');

      this.syncCombo();
      this.picked = { iso, name, remote };

      if (!go) return;
      const template = remote ? this.config.pickerGoRemote : this.config.pickerGoLocal;
      if (template) go.textContent = template.replace(/\{country\}/gi, name);
      if (remote) go.href = buildTarget({ url: this.config.remoteUrl }, this.config, iso);
    }

    /** Insignia circular con la bandera del país. */
    static chip(iso) {
      const chip = document.createElement('span');
      chip.className = 'venner-geo__chip';
      if (/^[A-Z]{2}$/.test(iso)) {
        const img = document.createElement('img');
        img.alt = '';
        img.loading = 'lazy';
        img.src = VennerGeo.flagUrl(iso);
        chip.append(img);
      }
      return chip;
    }

    /**
     * Desplegable propio construido a partir del <select>, que se queda como respaldo
     * accesible y como fuente de datos. Un <select> nativo no puede pintar una imagen
     * por opción, y eso es justo lo que hace falta para las banderas.
     */
    buildCombo() {
      const combo = document.createElement('div');
      combo.className = 'venner-geo__combo';

      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'venner-geo__combo-toggle';
      toggle.setAttribute('aria-haspopup', 'listbox');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.append(
        VennerGeo.chip(''),
        Object.assign(document.createElement('span'), { className: 'venner-geo__combo-value' })
      );
      toggle.insertAdjacentHTML(
        'beforeend',
        '<svg viewBox="0 0 12 8" width="12" aria-hidden="true"><path d="M1 1.5 6 6.5l5-5" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>'
      );

      const panel = document.createElement('div');
      panel.className = 'venner-geo__combo-panel';
      panel.setAttribute('role', 'listbox');
      panel.hidden = true;

      combo.append(toggle, panel);
      this.picker.before(combo);

      this.combo = combo;
      this.comboToggle = toggle;
      this.comboPanel = panel;

      toggle.addEventListener('click', () => this.toggleCombo());
      toggle.addEventListener('keydown', (event) => {
        if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
          event.preventDefault();
          this.toggleCombo(true);
        }
      });
      panel.addEventListener('keydown', (event) => this.onComboKey(event));
      document.addEventListener('click', (event) => {
        if (!combo.contains(event.target)) this.toggleCombo(false);
      });

      this.syncCombo();
    }

    /** Refleja en el botón lo que tenga seleccionado el <select>. */
    syncCombo() {
      if (!this.comboToggle) return;
      const option = this.picker.selectedOptions[0];
      if (!option) return;
      this.comboToggle.firstElementChild.replaceWith(VennerGeo.chip(option.value));
      this.comboToggle.querySelector('.venner-geo__combo-value').textContent = option.textContent.trim();
    }

    /** Las 34 banderas se piden hasta que alguien abre la lista, no al cargar la página. */
    fillCombo() {
      if (this.comboFilled) return;
      this.comboFilled = true;

      const check =
        '<svg class="venner-geo__check" viewBox="0 0 14 11" width="14" aria-hidden="true"><path d="M1 5.5 5 9.5 13 1.5" fill="none" stroke="currentColor" stroke-width="1.8"/></svg>';

      for (const group of this.picker.children) {
        if (group.tagName === 'OPTGROUP') {
          const title = document.createElement('p');
          title.className = 'venner-geo__combo-group';
          title.textContent = group.label;
          this.comboPanel.append(title);
        }

        for (const option of group.tagName === 'OPTGROUP' ? group.children : [group]) {
          const row = document.createElement('button');
          row.type = 'button';
          row.className = 'venner-geo__option';
          row.setAttribute('role', 'option');
          row.dataset.value = option.value;
          row.setAttribute('aria-selected', String(option.selected));
          row.append(
            VennerGeo.chip(option.value),
            Object.assign(document.createElement('span'), {
              className: 'venner-geo__option-name',
              textContent: option.textContent.trim()
            })
          );
          row.insertAdjacentHTML('beforeend', check);
          row.addEventListener('click', () => this.choose(option.value));
          this.comboPanel.append(row);
        }
      }
    }

    toggleCombo(force) {
      const open = force === undefined ? this.comboPanel.hidden : force;
      if (open) this.fillCombo();

      this.comboPanel.hidden = !open;
      this.combo.dataset.open = String(open);
      this.comboToggle.setAttribute('aria-expanded', String(open));
      if (!open) return;

      this.placePanel();

      const selected = this.comboPanel.querySelector('[aria-selected="true"]') ||
        this.comboPanel.querySelector('.venner-geo__option');
      if (selected) {
        selected.focus({ preventScroll: true });
        // scrollIntoView arrastraría también la tarjeta y el panel se movería de lugar:
        // el panel es el contenedor posicionado, así que basta con moverlo a él.
        this.comboPanel.scrollTop =
          selected.offsetTop - (this.comboPanel.clientHeight - selected.offsetHeight) / 2;
      }
    }

    /**
     * Hacia dónde se abre la lista y cuánto puede medir.
     *
     * La tarjeta tiene `overflow-y: auto`, así que recorta el panel: lo que sobresalga por
     * arriba queda fuera de alcance —no se puede subir más allá de scrollTop 0— y esos países
     * dejan de poder elegirse. Se mide el hueco real que queda dentro de la tarjeta de cada
     * lado, gana el lado con más aire y el alto se limita a ese hueco.
     */
    placePanel() {
      const toggle = this.comboToggle.getBoundingClientRect();
      const card = this.querySelector('.venner-geo__card');
      const box = card ? card.getBoundingClientRect() : null;

      // El recorte ocurre en el borde de la caja de relleno de la tarjeta, no en su contenido.
      const ceiling = Math.max(box ? box.top : 0, 0);
      const floor = Math.min(box ? box.bottom : window.innerHeight, window.innerHeight);

      // El margen de 6px del panel más un respiro para que no quede pegado al borde.
      const gap = 14;
      const above = toggle.top - ceiling - gap;
      const below = floor - toggle.bottom - gap;
      const up = above > below;

      this.combo.dataset.drop = up ? 'up' : 'down';
      this.comboPanel.style.setProperty(
        '--vg-panel-max',
        `${Math.round(Math.max(120, Math.min(310, up ? above : below)))}px`
      );
    }

    onComboKey(event) {
      const rows = [...this.comboPanel.querySelectorAll('.venner-geo__option')];
      const index = rows.indexOf(document.activeElement);

      if (event.key === 'Escape') {
        this.toggleCombo(false);
        this.comboToggle.focus();
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const next = event.key === 'ArrowDown' ? index + 1 : index - 1;
        (rows[next] || rows[event.key === 'ArrowDown' ? 0 : rows.length - 1]).focus();
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        (event.key === 'Home' ? rows[0] : rows[rows.length - 1]).focus();
      }
    }

    choose(iso) {
      this.picker.value = iso;
      this.comboPanel.querySelectorAll('.venner-geo__option').forEach((row) => {
        row.setAttribute('aria-selected', String(row.dataset.value === iso));
      });
      this.syncCombo();
      this.toggleCombo(false);
      this.comboToggle.focus();
      this.picker.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /** Deja el selector en el país donde está el visitante, no en el primero de la lista. */
    preselect(country) {
      if (!this.picker || !country) return;
      const match = [...this.picker.options].find((option) => option.value === country);
      if (!match) return;
      this.picker.value = country;
      this.syncCombo();
      if (this.comboFilled) {
        this.comboPanel.querySelectorAll('.venner-geo__option').forEach((row) => {
          row.setAttribute('aria-selected', String(row.dataset.value === country));
        });
      }
    }

    /**
     * A dónde pertenece un país, en orden de prioridad:
     *   1. Una regla escrita a mano en el theme editor (override).
     *   2. Los markets de ESTA tienda: ya está donde debe, no hay nada que sugerir.
     *   3. Los markets de la OTRA tienda: se le sugiere cambiarse.
     *   4. Ninguno de los dos: no hay cobertura, se le ofrece la tienda de respaldo.
     */
    resolve(country) {
      const cfg = this.config;

      const manual = cfg.rules.find((rule) => rule.countries.includes(country));
      if (manual) return manual;

      if ((cfg.localCountries || []).includes(country)) return null;

      if ((cfg.remoteCountries || []).includes(country) && !sameOrigin(cfg.remoteUrl)) {
        return { countries: [country], url: cfg.remoteUrl, label: cfg.remoteLabel, flagCode: country };
      }

      if (!cfg.fallbackUrl || sameOrigin(cfg.fallbackUrl)) return null;
      return {
        countries: [country],
        url: cfg.fallbackUrl,
        label: cfg.fallbackLabel,
        flagCode: country,
        unsupported: true
      };
    }

    render(country) {
      const fill = (value) =>
        value.replace(/\{country\}/gi, this.rule.label).replace(/\{code\}/gi, country);

      if (this.config.showFlag) this.renderFlag(country);

      this.querySelectorAll('[data-venner-geo-text]').forEach((node) => {
        node.textContent = fill(node.dataset.template || node.textContent);
      });

      // Sin cobertura: el mensaje y el botón cambian, y no tiene caso ofrecer un
      // selector de país al que ese visitante no pertenece.
      if (this.rule.unsupported) {
        const body = this.querySelector('[data-venner-geo-unsupported]');
        if (body) {
          body.hidden = false;
          body.textContent = this.config.unsupportedText.replace(/\{country\}/gi, this.countryName(country));
        }
        this.querySelectorAll('[data-venner-geo-hide-unsupported]').forEach((n) => (n.hidden = true));
        const go = this.querySelector('[data-venner-geo-go]');
        if (go && this.config.unsupportedButton) go.textContent = this.config.unsupportedButton;
      }

      // El selector arranca en el país del visitante, no en el primero de la lista.
      this.preselect(country);

      const link = this.querySelector('[data-venner-geo-go]');
      link.href = buildTarget(this.rule, this.config);
      return this;
    }

    /**
     * Bandera del país. Por orden de preferencia:
     *   1. La imagen que se haya subido a la regla desde el theme editor.
     *   2. El SVG oficial que Shopify sirve en su CDN para cualquier código ISO.
     *   3. El emoji del país, si el SVG no cargara.
     * En los tres casos se recorta a la forma con object-fit: cover.
     */
    renderFlag(country) {
      const wrap = this.querySelector('[data-venner-geo-flag]');
      if (!wrap) return;

      const iso = String(this.rule.flagCode || country).toUpperCase();
      if (!this.rule.flagImage && !/^[A-Z]{2}$/.test(iso)) return;

      const img = document.createElement('img');
      img.alt = '';
      img.loading = 'eager';
      img.decoding = 'async';
      img.src = this.rule.flagImage || VennerGeo.flagUrl(iso);
      img.addEventListener('error', () => this.renderFlagFallback(wrap, iso), { once: true });

      wrap.classList.remove('venner-geo__flag--code');
      wrap.replaceChildren(img);
    }

    /** Nombre del país en el idioma del navegador; si no se puede, el código ISO. */
    countryName(iso) {
      try {
        const names = new Intl.DisplayNames([document.documentElement.lang || 'en'], { type: 'region' });
        return names.of(iso) || iso;
      } catch (error) {
        return iso;
      }
    }

    static flagUrl(iso) {
      return `https://cdn.shopify.com/static/images/flags/${iso.toLowerCase()}.svg`;
    }

    /** Windows no dibuja emoji de bandera: ahí se queda la píldora con el código ISO. */
    renderFlagFallback(wrap, iso) {
      const slot = document.createElement('span');
      const platform =
        (navigator.userAgentData && navigator.userAgentData.platform) || navigator.userAgent || '';

      if (/Win/i.test(platform)) {
        wrap.classList.add('venner-geo__flag--code');
        slot.textContent = iso;
      } else {
        slot.textContent = String.fromCodePoint(
          0x1f1e6 + iso.charCodeAt(0) - 65,
          0x1f1e6 + iso.charCodeAt(1) - 65
        );
      }
      wrap.replaceChildren(slot);
    }

    open() {
      // La barra inferior no debe bloquear la navegación; la ventana centrada sí.
      if (this.config.layout === 'bar') {
        this.dialog.show();
      } else {
        this.dialog.showModal();
      }
      this.setAttribute('open', '');
      this.dataset.state = 'open';
    }

    close() {
      this.removeAttribute('open');
      setTimeout(() => this.dialog.close(), 300);
      this.settle();
    }

    go(automatic) {
      // Si el visitante eligió un país de ESTA tienda, no hay que cambiar de dominio.
      if (this.picked && !this.picked.remote) {
        this.submitCountry(this.picked.iso);
        return;
      }

      const chosen = this.picked && this.picked.remote ? this.picked.iso : null;
      const rule = chosen ? { url: this.config.remoteUrl } : this.rule;
      const url = buildTarget(rule, this.config, chosen);
      this.navigating = true;
      track(automatic ? 'auto_redirect' : 'accepted', { geo_target: url });
      this.remember('go');
      location.replace(url);
    }

    dismiss() {
      track('dismissed', {});
      // El cliente decidió quedarse: puede vivir en EE. UU. y querer enviar a México.
      this.remember('stay');
      this.close();
    }
  }

  if (!customElements.get('venner-geo')) customElements.define('venner-geo', VennerGeo);
})();
